import { shouldLogOutOnForbidden } from '@/lib/api/twoFactorEnrollment';
import { generalApi } from '@/lib/api/general';
import {
  dispatchCheckApiError,
  dispatchLogOut,
  MainContext,
} from '@/store/main/actionsMain';
import { commitAddNotification } from '@/store/main/mutations';
import { SortState } from '@/composables/useSds';
import { AxiosError } from 'axios';
import {
  GetDynamicListResultsRequestBody,
  UpdateDynamicListRequestBody,
} from '@/interfaces/api/general/dynamicList';
import { defineMessages } from '@/lib/i18n';
import { ApiErrorResponse } from '@/interfaces/api/general/errorResponses';

const m = defineMessages({
  requestDynamicListSuccess: 'リスト追加を依頼しました',
  updateDynamicListNameSuccess: 'リスト名を更新しました',
  updateDynamicListDescriptionSuccess: '概要を更新しました',
  checklistCreated: 'チェックリストを作成しました',
  checklistUpdatesResumed: 'チェックリストの更新を再開しました',
  checklistUpdatesSuspended: 'チェックリストの更新を停止しました',
  checklistDeleted: 'チェックリストを削除しました',
  favoriteAddSuccess: 'お気に入りに登録しました',
});

export const actionGetDynamicList = async (
  context: MainContext,
  payload: {
    dynamicListId: number;
  }
) => {
  try {
    return await generalApi.getDynamicList(payload.dynamicListId);
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
  }
};

export const actionUpdateDynamicList = async (
  context: MainContext,
  payload: {
    dynamicListId: number;
    data: UpdateDynamicListRequestBody;
  }
) => {
  try {
    const response = await generalApi.updateDynamicList(
      payload.dynamicListId,
      payload.data
    );
    if (response) {
      // Show appropriate message based on what was updated
      const messageKey =
        payload.data.description !== undefined
          ? m.updateDynamicListDescriptionSuccess
          : m.updateDynamicListNameSuccess;
      commitAddNotification(context, {
        messageKey,
        color: 'success',
      });
    }
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
  }
};

export const actionGetDynamicLists = async (
  context: MainContext,
  payload: {
    page: number;
    limit: number;
    showAll: boolean;
  }
) => {
  try {
    return await generalApi.getDynamicLists(
      payload.page,
      payload.limit,
      payload.showAll
    );
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
  }
};

export const actionGetDynamicListResults = async (
  context: MainContext,
  payload: {
    dynamicListId: number;
    data: GetDynamicListResultsRequestBody;
  }
) => {
  const {
    dynamicListId,
    data: { sortState, ...body },
  } = payload;

  const safeSortState: SortState | undefined =
    sortState === null ? undefined : sortState;

  try {
    return await generalApi.getDynamicListResults(dynamicListId, {
      ...body,
      sortState: safeSortState,
    });
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
  }
};

export const actionGetExportDynamicListResults = async (
  context: MainContext,
  payload: {
    dynamicListId: number;
  }
) => {
  try {
    return await generalApi.getExportDynamicListResults(payload.dynamicListId);
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
  }
};

export const actionRequestDynamicList = async (
  context: MainContext,
  payload: {
    companyName: string;
    description: string;
    law_regulation_text: string[];
    skipNotification?: boolean;
  }
) => {
  try {
    const data = {
      company_name: payload.companyName,
      description: payload.description,
      law_regulation_text: payload.law_regulation_text,
    };
    const response = await generalApi.requestDynamicList(data);
    if (response && !payload.skipNotification) {
      commitAddNotification(context, {
        messageKey: m.requestDynamicListSuccess,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
  }
};

export const actionCreateDynamicList = async (
  context: MainContext,
  payload: {
    lawRegulationIds: string[];
    extractionTargets: string[];
    title: string;
    description: string;
    dbt_types?: string[];
    law_ids?: number[];
    regulation_ids?: number[];
  }
) => {
  try {
    const response = await generalApi.createDynamicList({
      law_regulation_ids: payload.lawRegulationIds,
      extraction_targets: payload.extractionTargets,
      title: payload.title,
      description: payload.description,
      dbt_types: payload.dbt_types,
      law_ids: payload.law_ids,
      regulation_ids: payload.regulation_ids,
    });
    if (response) {
      commitAddNotification(context, {
        messageKey: m.checklistCreated,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionResumeDynamicLists = async (
  context: MainContext,
  payload: {
    dynamicListIds: number[];
  }
) => {
  try {
    const response = await generalApi.resumeDynamicLists({
      dynamic_list_ids: payload.dynamicListIds,
    });
    if (response) {
      commitAddNotification(context, {
        messageKey: m.checklistUpdatesResumed,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionSuspendDynamicLists = async (
  context: MainContext,
  payload: {
    dynamicListIds: number[];
  }
) => {
  try {
    const response = await generalApi.suspendDynamicLists({
      dynamic_list_ids: payload.dynamicListIds,
    });
    if (response) {
      commitAddNotification(context, {
        messageKey: m.checklistUpdatesSuspended,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionDeleteDynamicLists = async (
  context: MainContext,
  payload: {
    dynamicListIds: number[];
  }
) => {
  try {
    const response = await generalApi.deleteDynamicLists({
      dynamic_list_ids: payload.dynamicListIds,
    });
    if (response) {
      commitAddNotification(context, {
        messageKey: m.checklistDeleted,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

// SE-572 v2 通知機能: お気に入り(★)の一括登録
export const actionFavoriteDynamicLists = async (
  context: MainContext,
  payload: {
    dynamicListIds: number[];
  }
) => {
  try {
    const response = await generalApi.bulkFavoriteDynamicLists({
      dynamic_list_ids: payload.dynamicListIds,
      action: 'add',
    });
    if (response) {
      commitAddNotification(context, {
        messageKey: m.favoriteAddSuccess,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

// SE-572 v2 通知機能: お気に入り(★)の一括解除
export const actionUnfavoriteDynamicLists = async (
  context: MainContext,
  payload: {
    dynamicListIds: number[];
  }
) => {
  try {
    // SE-572 UX指摘①-B: 解除は確認ダイアログ廃止・即時実行。成功通知は呼び出し側の
    // Undo付きスナックバー(元に戻す)で行うため、ここでは通知しない。
    await generalApi.bulkFavoriteDynamicLists({
      dynamic_list_ids: payload.dynamicListIds,
      action: 'remove',
    });
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionGetDynamicListTemplates = async (
  context: MainContext,
  payload: {
    page?: number;
    limit?: number;
    country_code?: string;
    category_id?: number;
    label_id?: number;
    search?: string;
  }
) => {
  try {
    const result = await generalApi.getDynamicListTemplates(payload);
    return result;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    throw error;
  }
};

export const actionGetDynamicListTemplate = async (
  context: MainContext,
  payload: {
    templateId: number;
  }
) => {
  try {
    const result = await generalApi.getDynamicListTemplate(payload.templateId);
    return result;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    throw error;
  }
};

export const actionGetDynamicListCategories = async (context: MainContext) => {
  try {
    const result = await generalApi.getDynamicListTemplateCategories();
    return result;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    throw error;
  }
};

export const actionCreateDynamicListFromTemplate = async (
  context: MainContext,
  payload: {
    template_id: number;
    name_override?: string;
    description_override?: string;
  }
) => {
  try {
    const response = await generalApi.createDynamicListFromTemplate(payload);
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};
